import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { enqueue, isNetworkError } from "../../lib/offlineQueue";
import { ContactFields, EMPTY_CONTACT } from "./ContactFields";

// Exit PIN stops a guest holding your phone from leaving the sign-up sheet.
const PinSetup = ({ onSet, onCancel }) => {
  const [v, setV] = useState("");
  return (
    <div data-testid="kiosk-pin-setup" className="fixed inset-0 z-50 bg-brand-charcoal text-white flex items-center justify-center px-6">
      <form
        className="w-full max-w-xs text-center"
        onSubmit={(e) => { e.preventDefault(); if (/^\d{4}$/.test(v)) onSet(v); else toast.error("Use exactly 4 digits."); }}
      >
        <Lock className="h-8 w-8 text-brand-gold mx-auto" />
        <p className="mt-3 font-display font-black text-2xl">Set an exit PIN</p>
        <p className="mt-1 text-white/65 text-[13px]">You'll need it to leave sign-up mode, so guests can't open your admin.</p>
        <input
          autoFocus inputMode="numeric" maxLength={4} value={v}
          onChange={(e) => setV(e.target.value.replace(/\D/g, "").slice(0, 4))}
          data-testid="kiosk-pin-input"
          className="mt-5 w-full h-14 rounded-2xl bg-white text-brand-charcoal text-center text-3xl tracking-[0.5em] font-black"
        />
        <button type="submit" data-testid="kiosk-pin-start" className="mt-4 w-full h-12 rounded-full bg-brand-teal font-bold uppercase tracking-[0.12em] text-[13px]">Start sign-up sheet</button>
        <button type="button" onClick={onCancel} data-testid="kiosk-pin-cancel" className="mt-2 w-full h-10 text-white/60 text-[12px] font-bold uppercase tracking-[0.1em]">Cancel</button>
      </form>
    </div>
  );
};

// Full-screen sign-up sheet to hand your phone to guests at the booth.
export const KioskMode = ({ event, onSaved, onExit }) => {
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [pin, setPin] = useState(null);

  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setDone(false), 3500);
    return () => clearTimeout(t);
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please add an email or phone number.");
      return;
    }
    setBusy(true);
    const payload = { ...form, email: form.email.trim() || null, source: "kiosk" };
    try {
      const { data } = await api.post(`/admin/events/${event.id}/contacts`, payload, { timeout: 15000 });
      onSaved(data.contact);
      setForm(EMPTY_CONTACT);
      setDone(true);
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueue({ type: "contact", event_id: event.id, payload });
        setForm(EMPTY_CONTACT);
        setDone(true);
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const exit = () => {
    const entered = window.prompt("Enter your 4-digit exit PIN");
    if (entered === null) return;
    if (entered.trim() === pin) onExit();
    else toast.error("Wrong PIN");
  };

  if (!pin) return <PinSetup onSet={setPin} onCancel={onExit} />;

  return (
    <div data-testid="kiosk-mode" className="fixed inset-0 z-50 bg-brand-charcoal text-white overflow-y-auto">
      <div className="max-w-lg mx-auto px-5 pt-8 pb-16">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold">Fresh Fast Foods</p>
            <h1 className="mt-2 font-display font-black text-4xl leading-tight">Stay in touch</h1>
            <p className="mt-2 text-white/70 text-[15px]">{event.name} — leave your details and we&apos;ll follow up about catering.</p>
          </div>
          <button type="button" onClick={exit} data-testid="kiosk-exit" className="p-2.5 rounded-full bg-white/10 shrink-0" aria-label="Exit sign-up mode">
            <Lock className="h-4 w-4" />
          </button>
        </div>
        {done ? (
          <div data-testid="kiosk-thanks" className="mt-16 flex flex-col items-center text-center gap-4">
            <CheckCircle2 className="h-16 w-16 text-brand-gold" />
            <p className="font-display font-black text-3xl">Thank you!</p>
            <p className="text-white/70">We&apos;ll be in touch soon.</p>
            <button type="button" onClick={() => setDone(false)} data-testid="kiosk-next" className="mt-4 h-12 px-8 rounded-full bg-white text-brand-charcoal font-bold uppercase tracking-[0.1em] text-[13px]">
              Next guest
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 rounded-3xl bg-white text-brand-charcoal p-5" data-testid="kiosk-form">
            <ContactFields value={form} onChange={setForm} testidPrefix="kiosk" required={["name"]} large />
            <button type="submit" disabled={busy} data-testid="kiosk-submit" className="mt-5 w-full h-14 rounded-full bg-brand-teal text-white font-bold uppercase tracking-[0.12em] text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign me up
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default KioskMode;
